import { useState } from "react";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

/**
 * Hero credibility card on the public realtor dashboard.
 *
 * Goals (from realtor feedback):
 *  - Make the dashboard feel like a *portfolio*, not a faceless link.
 *  - Convince prospective sellers to list with this realtor.
 *
 * Shows:
 *  - Headshot + name + brokerage
 *  - Tagline ("Closing 2x faster than the local average")
 *  - Sales-record stat ("12 sold in last 90 days")
 *  - Up to 3 testimonials in a small carousel.
 */
export default function RealtorCredibilityCard({ realtor, brand }) {
  const [idx, setIdx] = useState(0);
  if (!realtor || !realtor.name) return null;

  const t = realtor.testimonials || [];
  const has = t.length > 0;
  const current = has ? t[Math.min(idx, t.length - 1)] : null;

  return (
    <div
      className="relative overflow-hidden rounded-3xl border bg-[#0A0A0A] p-6 md:p-8"
      style={{ borderColor: brand ? `${brand}50` : "rgb(255 255 255 / 0.1)" }}
      data-testid="realtor-credibility-card"
    >
      {/* Subtle accent gradient */}
      {brand && (
        <div
          className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 pointer-events-none"
          style={{ background: `radial-gradient(circle, ${brand} 0%, transparent 70%)` }}
        />
      )}
      <div className="relative grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 items-start">
        {/* Headshot */}
        {realtor.headshot_url && (
          <img
            src={`${PUBLIC_BASE}${realtor.headshot_url}`}
            alt={realtor.name}
            className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover shrink-0"
            style={
              brand
                ? { boxShadow: `0 0 0 3px ${brand}` }
                : { boxShadow: "0 0 0 3px rgba(255,255,255,0.1)" }
            }
            data-testid="realtor-credibility-headshot"
          />
        )}

        {/* Identity + tagline + contact */}
        <div className="min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.4em] mb-1"
            style={{ color: brand || "#71717a" }}
          >
            Listing presented by
          </div>
          <div className="font-display text-3xl md:text-4xl text-white leading-tight">
            {realtor.name}
          </div>
          {realtor.brokerage_name && (
            <div className="text-zinc-400 text-sm mt-1">
              {realtor.brokerage_name}
            </div>
          )}
          {realtor.tagline && (
            <div
              className="mt-3 text-base md:text-lg text-zinc-200 italic max-w-xl"
              data-testid="realtor-credibility-tagline"
            >
              “{realtor.tagline}”
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-zinc-400 mt-3">
            {realtor.phone && (
              <a href={`tel:${realtor.phone}`} className="hover:text-white inline-flex items-center gap-1">
                <Icons.Phone size={11} />
                {realtor.phone}
              </a>
            )}
            {realtor.email && (
              <a href={`mailto:${realtor.email}`} className="hover:text-white inline-flex items-center gap-1">
                <Icons.Mail size={11} />
                {realtor.email}
              </a>
            )}
            {realtor.website && (
              <a
                href={realtor.website}
                target="_blank"
                rel="noreferrer"
                className="hover:text-white inline-flex items-center gap-1"
                style={brand ? { color: brand } : undefined}
              >
                <Icons.Globe size={11} />
                {realtor.website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </div>

        {/* Sales-record / experience strip */}
        <div className="flex flex-row lg:flex-col gap-3 lg:items-end shrink-0 min-w-[140px]">
          {realtor.sales_count_90d > 0 && (
            <div
              className="bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-center min-w-[110px]"
              data-testid="realtor-credibility-sales"
            >
              <div
                className="font-display text-3xl"
                style={{ color: brand || "#ffffff" }}
              >
                {realtor.sales_count_90d}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                sold · 90 days
              </div>
            </div>
          )}
          {realtor.years_experience > 0 && (
            <div
              className="bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-center min-w-[110px]"
              data-testid="realtor-credibility-experience"
            >
              <div
                className="font-display text-3xl"
                style={{ color: brand || "#ffffff" }}
              >
                {realtor.years_experience}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                years exp.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Testimonials carousel */}
      {has && (
        <div
          className="relative mt-6 pt-6 border-t border-white/10"
          data-testid="realtor-credibility-testimonials"
        >
          <div className="flex items-start gap-3">
            <Icons.Quote
              size={20}
              className="shrink-0 mt-1"
              style={{ color: brand || "#52525b" }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm md:text-base text-zinc-200 italic leading-relaxed">
                {current.quote}
              </div>
              {(current.author || current.role) && (
                <div className="text-xs text-zinc-500 mt-2">
                  — {current.author}
                  {current.role && (
                    <span className="text-zinc-600">, {current.role}</span>
                  )}
                </div>
              )}
            </div>
            {t.length > 1 && (
              <div className="flex gap-1 shrink-0 mt-1">
                <button
                  onClick={() => setIdx((i) => (i - 1 + t.length) % t.length)}
                  className="p-1.5 rounded-md hover:bg-white/5 text-zinc-500 hover:text-white"
                  data-testid="realtor-credibility-testimonial-prev"
                >
                  <Icons.ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % t.length)}
                  className="p-1.5 rounded-md hover:bg-white/5 text-zinc-500 hover:text-white"
                  data-testid="realtor-credibility-testimonial-next"
                >
                  <Icons.ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
          {t.length > 1 && (
            <div className="flex gap-1.5 mt-3">
              {t.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-6" : "w-1.5"
                  }`}
                  style={{
                    background:
                      i === idx
                        ? brand || "#ffffff"
                        : "rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
